import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { DashboardService, PayoffQuoteService } from '@ld.consumer-portal/data-access';
import { EnvironmentFeatureService, LoanSelectionService } from '@ld.consumer-portal/utils';
import { catchError, map, of } from 'rxjs';

export const payoffQuoteGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const router = inject(Router);
  const envFeatureService = inject(EnvironmentFeatureService);
  const loanSelectionService = inject(LoanSelectionService);
  const payoffQuoteService = inject(PayoffQuoteService);
  const dashboardService = inject(DashboardService);

  if (!envFeatureService.isEnabled('PayoffQuoteRequest')) {
    return router.createUrlTree(['/dashboard']);
  }

  const routeAccountId = route.parent?.paramMap.get('accountId');
  const selectedLoanNumber = loanSelectionService.selectedLoanNumber();
  const loanNumber = selectedLoanNumber ?? routeAccountId;

  const isHeloc = localStorage.getItem('isHeloc') === 'true';
  if (isHeloc) {
    payoffQuoteService.showIneligiblePopup.set(true);
    return router.createUrlTree(['/loans', loanNumber ?? '', 'details']);
  }

  if (loanNumber && payoffQuoteService.isMarkedNotEligible(loanNumber)) {
    payoffQuoteService.showIneligiblePopup.set(true);
    return router.createUrlTree(['/loans', loanNumber, 'details']);
  }

  return dashboardService.getUserAccounts().pipe(
    map(() => {
      let selectedLoan = loanSelectionService.selectedLoan();

      if (!selectedLoan && loanNumber) {
        loanSelectionService.selectLoanById(loanNumber);
        selectedLoan = loanSelectionService.selectedLoan();
      }

      const isHelocLoan = loanSelectionService.isHeloc();
      if (isHelocLoan) {
        if (selectedLoan) {
          payoffQuoteService.markLoanAsNotEligible(selectedLoan.loanNumber);
        }
        payoffQuoteService.showIneligiblePopup.set(true);
        return router.createUrlTree(['/loans', selectedLoan?.loanNumber ?? loanNumber ?? '', 'details']);
      }

      if (selectedLoan) {
        const loanType = loanSelectionService.selectedLoanType() ?? selectedLoan.productType;
        const eligibility = payoffQuoteService.checkEligibility(
          selectedLoan.loanNumber,
          loanType,
          selectedLoan.canRequestPayoffQuote
        );

        if (!eligibility.canRequestPayoffQuote) {
          payoffQuoteService.markLoanAsNotEligible(selectedLoan.loanNumber);
          payoffQuoteService.showIneligiblePopup.set(true);
          return router.createUrlTree(['/loans', selectedLoan.loanNumber, 'details']);
        }
      }

      return true;
    }),
    catchError(() => {
      return of(router.createUrlTree(['/loans', loanNumber ?? '', 'details']));
    })
  );
};
